// European Roulette wheel order (37 numbers: 0-36)
export const WHEEL_NUMBERS = [
  0, 32, 15, 19, 4, 21, 2, 25, 17, 34, 6, 27, 13, 36, 11, 30, 8, 23, 10, 5,
  24, 16, 33, 1, 20, 14, 31, 9, 22, 18, 29, 7, 28, 12, 35, 3, 26
]

// Red numbers in European roulette
export const RED_NUMBERS = [1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36]

// Black numbers in European roulette
export const BLACK_NUMBERS = [2, 4, 6, 8, 10, 11, 13, 15, 17, 20, 22, 24, 26, 28, 29, 31, 33, 35]

// Bet types
export type RuletaBetType =
  | 'straight' // Single number (35:1)
  | 'split' // Two numbers (17:1)
  | 'street' // Three numbers (11:1)
  | 'corner' // Four numbers (8:1)
  | 'line' // Six numbers (5:1)
  | 'column' // 12 numbers column (2:1)
  | 'dozen' // 12 numbers 1-12, 13-24, 25-36 (2:1)
  | 'red' // Red (1:1)
  | 'black' // Black (1:1)
  | 'even' // Even (1:1)
  | 'odd' // Odd (1:1)
  | 'low' // 1-18 (1:1)
  | 'high' // 19-36 (1:1)

export interface RuletaBet {
  type: RuletaBetType
  numbers: number[]
  amount: number
  position?: string // For visual placement on board
}

// Get color of a number
export function getNumberColor(num: number): 'red' | 'black' | 'green' {
  if (num === 0) return 'green'
  return RED_NUMBERS.includes(num) ? 'red' : 'black'
}

// Get payout multiplier for bet type
export function getPayoutMultiplier(betType: RuletaBetType): number {
  switch (betType) {
    case 'straight':
      return 35
    case 'split':
      return 17
    case 'street':
      return 11
    case 'corner':
      return 8
    case 'line':
      return 5
    case 'column':
    case 'dozen':
      return 2
    case 'red':
    case 'black':
    case 'even':
    case 'odd':
    case 'low':
    case 'high':
      return 1
    default:
      return 0
  }
}

// Check if bet wins
export function checkBetWins(bet: RuletaBet, winningNumber: number): boolean {
  return bet.numbers.includes(winningNumber)
}

// Calculate winnings for a bet
export function calculateBetWinnings(bet: RuletaBet, winningNumber: number): number {
  if (!checkBetWins(bet, winningNumber)) return 0
  const multiplier = getPayoutMultiplier(bet.type)
  return bet.amount * (multiplier + 1) // Return bet + winnings
}

// Calculate total winnings for all bets
export function calculateTotalWinnings(bets: RuletaBet[], winningNumber: number): number {
  return bets.reduce((total, bet) => total + calculateBetWinnings(bet, winningNumber), 0)
}

// Get numbers for column bet (1, 2, or 3)
export function getColumnNumbers(column: 1 | 2 | 3): number[] {
  const numbers: number[] = []
  for (let i = column; i <= 36; i += 3) {
    numbers.push(i)
  }
  return numbers
}

// Get numbers for dozen bet (1, 2, or 3)
export function getDozenNumbers(dozen: 1 | 2 | 3): number[] {
  const start = (dozen - 1) * 12 + 1
  const numbers: number[] = []
  for (let i = start; i < start + 12; i++) {
    numbers.push(i)
  }
  return numbers
}

// Get numbers for outside bets
export function getOutsideBetNumbers(betType: 'red' | 'black' | 'even' | 'odd' | 'low' | 'high'): number[] {
  switch (betType) {
    case 'red':
      return RED_NUMBERS
    case 'black':
      return BLACK_NUMBERS
    case 'even':
      return Array.from({ length: 18 }, (_, i) => (i + 1) * 2)
    case 'odd':
      return Array.from({ length: 18 }, (_, i) => i * 2 + 1)
    case 'low':
      return Array.from({ length: 18 }, (_, i) => i + 1)
    case 'high':
      return Array.from({ length: 18 }, (_, i) => i + 19)
    default:
      return []
  }
}

// Get street numbers (row of 3)
export function getStreetNumbers(row: number): number[] {
  const start = (row - 1) * 3 + 1
  return [start, start + 1, start + 2]
}

// Get line numbers (two rows of 3 = 6 numbers)
export function getLineNumbers(row: number): number[] {
  return [...getStreetNumbers(row), ...getStreetNumbers(row + 1)]
}

// Get corner numbers
export function getCornerNumbers(topLeft: number): number[] {
  return [topLeft, topLeft + 1, topLeft + 3, topLeft + 4]
}

// Get split numbers (horizontal or vertical)
export function getSplitNumbers(num1: number, num2: number): number[] {
  return [num1, num2].sort((a, b) => a - b)
}

// Generate random winning number
export function generateWinningNumber(): number {
  return Math.floor(Math.random() * 37)
}

// Calculate wheel rotation for number
export function calculateWheelRotation(number: number): number {
  const index = WHEEL_NUMBERS.indexOf(number)
  const degreesPerNumber = 360 / 37
  // Base rotation + extra spins for visual effect
  const extraSpins = 5 + Math.random() * 3
  return extraSpins * 360 + index * degreesPerNumber
}

// Format bet type for display
export function formatBetType(bet: RuletaBet): string {
  switch (bet.type) {
    case 'straight':
      return `${bet.numbers[0]}`
    case 'split':
      return `${bet.numbers[0]}-${bet.numbers[1]}`
    case 'street':
      return `Calle ${Math.ceil(bet.numbers[0] / 3)}`
    case 'corner':
      return `Esquina ${bet.numbers[0]}`
    case 'line':
      return `Línea ${Math.ceil(bet.numbers[0] / 3)}-${Math.ceil(bet.numbers[0] / 3) + 1}`
    case 'column':
      return `Columna ${bet.numbers[0] % 3 || 3}`
    case 'dozen':
      return bet.numbers[0] <= 12 ? '1-12' : bet.numbers[0] <= 24 ? '13-24' : '25-36'
    case 'red':
      return 'Rojo'
    case 'black':
      return 'Negro'
    case 'even':
      return 'Par'
    case 'odd':
      return 'Impar'
    case 'low':
      return '1-18'
    case 'high':
      return '19-36'
    default:
      return ''
  }
}
